import { IonIcon } from '@ionic/react'
import { ModalOption } from '@vae/server/src/types/notifications'
import { alert, arrowRedo } from 'ionicons/icons'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { ModalContext } from '../../../../../contexts/ModalContext'
import { ObjectContext, ObjectContextType } from '../../../../../contexts/objectContext/ObjectContext'
import { UserContext } from '../../../../../contexts/UserContext'
import { ContractInformationMode } from '../../../../contractInformation/ContractInformation'

type Mode = 'consumption' | 'production'

interface Props {
	mode: Mode
	panelLinked: boolean
	productionAuthorized: boolean
}

type BannerTarget =
	| { type: 'route'; path: string; state?: { initialMode: ContractInformationMode } }
	| { type: 'modal'; key: ModalOption }

interface Banner {
	message: string
	target: BannerTarget
}

const isElectricityTransferPriceFilled = (object: ObjectContextType) => {
	const type = object.electricityTransferPriceContractType || 'fixed'
	if (type === 'fixed') {
		return !!object.electricityTransferPricePerKwh
	}
	if (type === 'nightAndDay') {
		return (
			!!object.electricityTransferPriceContractDayPrice &&
			!!object.electricityTransferPriceContractNightPrice
		)
	}
	return false
}

const ConsumptionChartBanners: React.FC<Props> = ({ mode, panelLinked, productionAuthorized }) => {
	const { insertModal } = useContext(ModalContext)
	const history = useHistory()
	const object = useContext(ObjectContext)
	const { isAnonymousUser } = useContext(UserContext)
	const { t } = useTranslation('pages_electricity', { keyPrefix: 'bannerMessage' })

	if (isAnonymousUser) return null

	const banners: Banner[] = []

	if (productionAuthorized && !panelLinked) {
		// Inverter needs to be linked before production data can be shown
		banners.push({
			message: t('case4'),
			target: { type: 'route', path: '/devices/add-device/solar-panel' },
		})
	} else if (panelLinked && !productionAuthorized) {
		// Inverter is linked, but the user still needs to authorize data sharing
		banners.push({
			message: mode === 'consumption' ? t('case2.cons') : t('case2.prod'),
			target: { type: 'modal', key: 'dhInstructions' },
		})
	}

	if (mode === 'consumption' && !isElectricityTransferPriceFilled(object)) {
		banners.push({
			message: t('noElectricityTransferPrice'),
			target: { type: 'route', path: '/contract', state: { initialMode: 'options' } },
		})
	}

	if (banners.length === 0) return null

	const handleClick = (target: BannerTarget) => {
		if (target.type === 'route') {
			history.push({ pathname: target.path, state: target.state })
		} else {
			insertModal(target.key)
		}
	}

	return (
		<>
			{banners.map(({ message, target }) => (
				<div
					key={message}
					className='production-banner relative z-0 hover'
					onClick={() => handleClick(target)}
				>
					<IonIcon icon={alert} />
					{message}
					<IonIcon icon={arrowRedo} />
				</div>
			))}
		</>
	)
}

export default ConsumptionChartBanners
